import * as React from 'react';
import { cn } from '../../Helpers/cn';

/**
 * Table component
 * @param {React.ComponentProps<'table'>} props - Component props
 * @returns {React.ReactElement} Table component
 */
function Table({ className, ...props }) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        className={cn('w-full caption-bottom', className)}
        {...props}
      />
    </div>
  );
}

/**
 * TableHeader component
 * @param {React.ComponentProps<'thead'>} props - Component props
 * @returns {React.ReactElement} TableHeader component
 */
function TableHeader({ className, ...props }) {
  return (
    <thead data-slot="table-header" className={cn('', className)} {...props} />
  );
}

/**
 * TableBody component
 * @param {React.ComponentProps<'tbody'>} props - Component props
 * @returns {React.ReactElement} TableBody component
 */
function TableBody({ className, ...props }) {
  return (
    <tbody data-slot="table-body" className={cn('', className)} {...props} />
  );
}

/**
 * TableFooter component
 * @param {React.ComponentProps<'tfoot'>} props - Component props
 * @returns {React.ReactElement} TableFooter component
 */
function TableFooter({ className, ...props }) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('font-medium', className)}
      {...props}
    />
  );
}

/**
 * TableRow component
 * @param {React.ComponentProps<'tr'>} props - Component props
 * @returns {React.ReactElement} TableRow component
 */
function TableRow({ className, ...props }) {
  return <tr data-slot="table-row" className={cn('', className)} {...props} />;
}

/**
 * TableHead component
 * @param {React.ComponentProps<'th'>} props - Component props
 * @returns {React.ReactElement} TableHead component
 */
function TableHead({ className, ...props }) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'h-10 px-2 text-left align-middle font-bold whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className
      )}
      {...props}
    />
  );
}

/**
 * TableCell component
 * @param {React.ComponentProps<'td'>} props - Component props
 * @returns {React.ReactElement} TableCell component
 */
function TableCell({ className, ...props }) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className
      )}
      {...props}
    />
  );
}

/**
 * TableCaption component
 * @param {React.ComponentProps<'caption'>} props - Component props
 * @returns {React.ReactElement} TableCaption component
 */
function TableCaption({ className, ...props }) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-sm text-gray-500/75', className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
